import type { LocalRepository, LocalStore } from '@ims/local'
import { tenantApi, type MemberRole, type TenantMembership } from '@ims/remote'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { clearTenantDrafts } from './lib/drafts.ts'
import { forgetUnsynced } from './lib/offline.ts'
import { TabLockedError, type OpenedStore, type StorageStatus } from './platform.ts'
import { useSession, useUser } from './session.tsx'

// The signed-in user's businesses (tenants), the one currently open, and its
// local store. Memberships are cached so the app can start offline.

const ROLE_RANK: Record<MemberRole, number> = {
  viewer: 1,
  waiter: 2,
  staff: 3,
  manager: 4,
  admin: 5,
  owner: 6,
}

type StoreState =
  | { status: 'none' }
  | { status: 'opening' }
  | { status: 'locked' }
  | { status: 'handedOver' }
  | { status: 'error'; message: string }
  | { status: 'ready'; store: LocalStore }

interface WorkspaceApi {
  memberships: TenantMembership[]
  membershipsLoaded: boolean
  current: TenantMembership | null
  storeState: StoreState
  storage: StorageStatus | null
  /** Asks the browser again to keep offline data (e.g. after installing the app). */
  refreshStorage(): Promise<void>
  selectTenant(tenantId: string): void
  refreshMemberships(): Promise<TenantMembership[]>
  takeOver(): void
  reopen(): void
  /** Deletes local data for every business of this user, then signs out. */
  signOut(): Promise<void>
}

const WorkspaceContext = createContext<WorkspaceApi | null>(null)

const cacheKey = (userId: string) => `ims.memberships.${userId}`
const lastTenantKey = (userId: string) => `ims.tenant.${userId}`

function readCache(userId: string): TenantMembership[] {
  try {
    return JSON.parse(localStorage.getItem(cacheKey(userId)) ?? '[]') as TenantMembership[]
  } catch {
    return []
  }
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { platform, signOutAuth } = useSession()
  const user = useUser()
  const [memberships, setMemberships] = useState<TenantMembership[]>(() => readCache(user.id))
  const [membershipsLoaded, setMembershipsLoaded] = useState(false)
  const [tenantId, setTenantId] = useState<string | null>(() =>
    localStorage.getItem(lastTenantKey(user.id)),
  )
  const [opened_, setOpened] = useState<{ tenantId: string; n: number; state: StoreState } | null>(
    null,
  )
  const [storage, setStorage] = useState<StorageStatus | null>(null)
  const [openRequest, setOpenRequest] = useState<{ takeOver: boolean; n: number }>({
    takeOver: false,
    n: 0,
  })
  const opened = useRef<OpenedStore | null>(null)
  // Opens run one after another, so a re-run (StrictMode, tenant switch) never
  // races the previous one for the tab lock.
  const openChain = useRef<Promise<void>>(Promise.resolve())

  const refreshMemberships = useCallback(async () => {
    const list = await tenantApi(platform.supabase).myTenants()
    localStorage.setItem(cacheKey(user.id), JSON.stringify(list))
    setMemberships(list)
    return list
  }, [platform.supabase, user.id])

  useEffect(() => {
    let active = true
    tenantApi(platform.supabase)
      .myTenants()
      .then(
        (list) => {
          if (!active) return
          localStorage.setItem(cacheKey(user.id), JSON.stringify(list))
          setMemberships(list)
          setMembershipsLoaded(true)
        },
        () => active && setMembershipsLoaded(true), // offline: keep the cached list
      )
    return () => {
      active = false
    }
  }, [platform.supabase, user.id])

  // Pick the remembered business, or the only one.
  const current = useMemo(() => {
    const found = memberships.find((m) => m.tenant_id === tenantId)
    if (found) return found
    return memberships.length === 1 ? memberships[0]! : null
  }, [memberships, tenantId])

  // Open (or re-open) the current business's local store. Keyed by the tenant
  // id, not the membership object: refreshing the list (e.g. after a rename)
  // must not close a store that screens are still using.
  const currentTenantId = current?.tenant_id ?? null
  useEffect(() => {
    if (!currentTenantId) return
    let cancelled = false
    const tag = { tenantId: currentTenantId, n: openRequest.n }
    const setStoreState = (state: StoreState) => setOpened({ ...tag, state })
    const run = async () => {
      await opened.current?.release()
      opened.current = null
      try {
        const handle = await platform.openStore(user.id, currentTenantId, {
          takeOver: openRequest.takeOver,
          onTakeoverRequested: () => {
            void handle.release().then(() => setStoreState({ status: 'handedOver' }))
            opened.current = null
          },
        })
        if (cancelled) {
          await handle.release()
          return
        }
        opened.current = handle
        handle.store.engine.start()
        setStoreState({ status: 'ready', store: handle.store })
        platform
          .requestPersistentStorage()
          .then(setStorage)
          .catch(() => {})
      } catch (error) {
        if (cancelled) return
        if (error instanceof TabLockedError) setStoreState({ status: 'locked' })
        else
          setStoreState({
            status: 'error',
            message: error instanceof Error ? error.message : String(error),
          })
      }
    }
    openChain.current = openChain.current.then(run, run)
    return () => {
      cancelled = true
    }
  }, [currentTenantId, platform, user.id, openRequest])

  const storeState = useMemo<StoreState>(() => {
    if (!current) return { status: 'none' }
    if (opened_ && opened_.tenantId === current.tenant_id && opened_.n === openRequest.n)
      return opened_.state
    return { status: 'opening' }
  }, [current, opened_, openRequest.n])

  // The server renames the tenant when business settings sync (a trigger on
  // tenant_settings), so refresh the business list after such a sync.
  useEffect(() => {
    if (storeState.status !== 'ready') return
    const { engine, repository } = storeState.store
    let settingsChanged = false
    const offTables = repository.subscribe((tables) => {
      if (tables.includes('tenant_settings')) settingsChanged = true
    })
    const offState = engine.subscribe((state) => {
      if (state.phase === 'idle' && state.pending === 0 && settingsChanged) {
        settingsChanged = false
        refreshMemberships().catch(() => {}) // offline: try again after the next sync
      }
    })
    return () => {
      offTables()
      offState()
    }
  }, [storeState, refreshMemberships])

  // Sync triggers (reconnect, focus) for the open store.
  useEffect(() => {
    if (storeState.status !== 'ready') return
    const { engine } = storeState.store
    return platform.attachSyncTriggers(() => engine.trigger())
  }, [storeState, platform])

  // Installed apps usually get protected storage: ask again once installed.
  useEffect(
    () =>
      platform.install.subscribe(() => {
        if (platform.install.installed())
          platform
            .requestPersistentStorage()
            .then(setStorage)
            .catch(() => {})
      }),
    [platform],
  )

  // Release on unmount (sign-out, page unload is handled by the browser).
  useEffect(
    () => () => {
      void opened.current?.release()
    },
    [],
  )

  const api = useMemo<WorkspaceApi>(
    () => ({
      memberships,
      membershipsLoaded,
      current,
      storeState,
      storage,
      async refreshStorage() {
        setStorage(await platform.requestPersistentStorage())
      },
      selectTenant(id) {
        localStorage.setItem(lastTenantKey(user.id), id)
        setTenantId(id)
      },
      refreshMemberships,
      takeOver: () => setOpenRequest((r) => ({ takeOver: true, n: r.n + 1 })),
      reopen: () => setOpenRequest((r) => ({ takeOver: false, n: r.n + 1 })),
      async signOut() {
        await opened.current?.release()
        opened.current = null
        setOpened(null)
        for (const m of memberships) {
          await platform.deleteLocalData(user.id, m.tenant_id).catch(() => {})
          forgetUnsynced(user.id, m.tenant_id)
          // A draft holds costs and suppliers: it goes with the rest of the local data.
          clearTenantDrafts(m.tenant_id)
        }
        localStorage.removeItem(cacheKey(user.id))
        localStorage.removeItem(lastTenantKey(user.id))
        await signOutAuth()
      },
    }),
    [
      memberships,
      membershipsLoaded,
      current,
      storeState,
      storage,
      refreshMemberships,
      platform,
      user.id,
      signOutAuth,
    ],
  )

  return <WorkspaceContext.Provider value={api}>{children}</WorkspaceContext.Provider>
}

export function useWorkspace(): WorkspaceApi {
  const api = useContext(WorkspaceContext)
  if (!api) throw new Error('useWorkspace must be used inside <WorkspaceProvider>')
  return api
}

export function useStore(): LocalStore {
  const { storeState } = useWorkspace()
  if (storeState.status !== 'ready') throw new Error('store is not open')
  return storeState.store
}

export function useRepo(): LocalRepository {
  return useStore().repository
}

export function useRole(): MemberRole {
  const { current } = useWorkspace()
  return current?.role ?? 'viewer'
}

export function roleAtLeast(role: MemberRole, min: MemberRole): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[min]
}

/** True when the current user's role is at least `min`. UI only: the server enforces roles. */
export function useCan(min: MemberRole): boolean {
  return roleAtLeast(useRole(), min)
}
